import type {
  Icd10RafWeightsRequest,
  Icd10RafWeightsResponse,
} from '@seen/request-types/icd10RafWeights'
import type {
  RafScoreRequest,
  RafScoreResponse,
} from '@seen/request-types/rafScore'
import type {
  RefreshQueuedEditsSummaryRequest,
  RefreshQueuedEditsSummaryResponse,
} from '@seen/request-types/refreshQueuedEditsSummary'
import type {
  EvaluateSchedulingRulesRequest,
  EvaluateSchedulingRulesResponse,
  SuggestEngineSlotsRequest,
  SuggestEngineSlotsResponse,
} from '@seen/request-types/schedulingRules'
import type {
  AppointmentSlotNotSelected,
  AppointmentSlotSelected,
  SuggestAppointmentSlotsRequest,
  SuggestAppointmentSlotsResponse,
  SuggestionFeedback,
} from '@seen/request-types/suggestAppointmentSlots'
import type {
  SuggestIcd10Request,
  SuggestIcd10Response,
} from '@seen/request-types/suggestIcd10'
import type { AxiosInstance } from 'axios'
import { createContext } from 'react'
import type {
  NpiAffiliation,
  NpiOrgCandidate,
  NpiSuggestion,
} from '#src/components/practitioner/npi-types.ts'

// AI calls served by the host app's same-origin AI server. The portal injects
// an adapter built on its typed RPC client; apps without AI routes omit it.
// Contract types come from @seen/request-types so this package never depends
// on the server or its RPC client types.
export interface SeenAiApi {
  suggestIcd10: (request: SuggestIcd10Request) => Promise<SuggestIcd10Response>
  suggestAppointmentSlots: (
    request: SuggestAppointmentSlotsRequest
  ) => Promise<SuggestAppointmentSlotsResponse>
  // Fire-and-forget telemetry: the scheduler picked one of the suggested slots.
  recordAppointmentSlotSelected: (
    request: AppointmentSlotSelected
  ) => Promise<void>
  // Fire-and-forget telemetry: the scheduler booked a slot that was not one of
  // the suggestions (a disagreement signal, not a hard negative).
  recordAppointmentSlotNotSelected: (
    request: AppointmentSlotNotSelected
  ) => Promise<void>
  // Fire-and-forget telemetry: the scheduler thumbed a suggestion set up/down.
  recordSuggestionFeedback: (request: SuggestionFeedback) => Promise<void>
  refreshQueuedEditsSummary: (
    request: RefreshQueuedEditsSummaryRequest
  ) => Promise<RefreshQueuedEditsSummaryResponse>
  rafScore: (request: RafScoreRequest) => Promise<RafScoreResponse>
  icd10RafWeights: (
    request: Icd10RafWeightsRequest,
    options?: { signal?: AbortSignal }
  ) => Promise<Icd10RafWeightsResponse>
  // Rules-engine window evaluation: the avoid-time geometry and rule
  // provenance for a candidate booking's practitioners over a date range.
  evaluateSchedulingRules: (
    request: EvaluateSchedulingRulesRequest
  ) => Promise<EvaluateSchedulingRulesResponse>
  // Deterministic engine-generated "Next Available" candidates, ranked by
  // warning load then earliness. Unlike the AI suggester, no model is involved.
  suggestEngineSchedulingSlots: (
    request: SuggestEngineSlotsRequest
  ) => Promise<SuggestEngineSlotsResponse>
}

// NPI-registry lookups served by the host app's same-origin NPPES proxy.
// Presence gates registry-backed creation flows (e.g. the Send Fax modal's
// create-recipient wizard).
export interface SeenNpiApi {
  searchIndividuals: (query: string) => Promise<NpiSuggestion[]>
  searchOrganizations: (params: {
    q?: string
    number?: string
    city?: string
    state?: string
    postalCode?: string
  }) => Promise<NpiOrgCandidate[]>
  getAffiliations: (npi: string) => Promise<NpiAffiliation[]>
}

export type SeenApi = {
  seenApi: AxiosInstance
  // AI capabilities injected by the host app; undefined when the app does not
  // serve AI routes. Presence is the single signal that AI is available --
  // `aiEnabled` is derived from it.
  ai: SeenAiApi | undefined
  aiEnabled: boolean
  // NPI-registry capability injected by the host app; undefined when the app
  // does not serve the NPPES proxy routes.
  npi: SeenNpiApi | undefined
  setMedplumAccessToken: (accessToken: string | undefined) => void
  appVersion: string
  appCommitSha: string
}

export const SeenApiContext = createContext<SeenApi | null>(null)
